import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Download, Loader2, Search, Check, X } from "lucide-react";
import { _t, _locale, getLang } from "@/lib/i18n";
import { AdminTabs } from "./AdminConsent";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(_locale(), { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const Yes = ({ ok }) => (ok ? <Check className="w-4 h-4 text-emerald-400 inline" /> : <X className="w-4 h-4 text-red-400 inline" />);
const COOKIE = { all: "Tutti accettati", necessary: "Solo necessari", custom: "Personalizzati" };

export default function AdminUsers() {
  const { user, loading: authLoading } = useAuth();
  const [q, setQ] = useState("");
  const [hideTest, setHideTest] = useState(true);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) return;
    const id = setTimeout(() => {
      api.get("/admin/users", { params: { q, hide_test: hideTest, page, limit: 50 } })
        .then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
    }, 250);
    return () => clearTimeout(id);
  }, [user, q, hideTest, page]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get("/admin/users/export", { params: { q, hide_test: hideTest, lang: getLang() }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `utenti_timecapsule_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(formatApiError(e)); } finally { setExporting(false); }
  };

  if (authLoading) return null;
  if (!user?.is_admin) return <Navigate to="/dashboard" replace />;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <AppLayout>
      <AdminTabs current="users" />
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF5722] mb-3">{_t("Admin · Utenti")}</div>
          <h1 className="font-display text-5xl text-white leading-tight mb-2">{_t("Utenti registrati")}</h1>
          <p className="text-white/60 max-w-2xl text-sm">{_t("Le password sono cifrate in modo irreversibile e non sono visibili: viene mostrata solo la data dell'ultimo cambio.")}</p>
        </div>
        <button data-testid="admin-users-export" onClick={exportXlsx} disabled={exporting} className="btn-launch px-5 py-2.5 text-sm inline-flex items-center gap-2">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}{_t("Esporta Excel")}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="admin-users-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder={_t("Cerca email, username o nome")} className="tc-input w-80" style={{ paddingLeft: "2.25rem" }} />
        </div>
        <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
          <input data-testid="admin-users-hide-test" type="checkbox" checked={hideTest} onChange={(e) => { setHideTest(e.target.checked); setPage(1); }} />
          {_t("Nascondi account di test")}
        </label>
        <span data-testid="admin-users-total" className="text-sm text-white/50 ml-auto">{data ? _t("{0} utenti", data.total) : ""}</span>
      </div>

      {!data ? <Loader2 className="w-8 h-8 text-[#FF5722] animate-spin mx-auto my-16" /> : (
        <div className="tc-card overflow-x-auto" data-testid="admin-users-table">
          <table className="w-full text-xs text-white/80">
            <thead className="text-[10px] uppercase tracking-[0.15em] text-white/50 border-b border-white/10">
              <tr>{["Username", "Email", "Registrazione", "Ultimo cambio password", "Email confermata", "Termini e Privacy", "Account", "Cookie", "Consensi alla registrazione"].map((h) => <th key={h} className="text-left px-3 py-3 align-bottom">{_t(h)}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {data.users.map((u) => <UserRow key={u.id} u={u} />)}
            </tbody>
          </table>
          {data.users.length === 0 && <div className="text-sm text-white/40 p-6">{_t("Nessun utente trovato.")}</div>}
        </div>
      )}

      {data && pages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6 text-sm text-white/70">
          <button data-testid="admin-users-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 rounded-full border border-white/15 disabled:opacity-30">‹</button>
          <span data-testid="admin-users-page">{page} / {pages}</span>
          <button data-testid="admin-users-next" disabled={page >= pages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 rounded-full border border-white/15 disabled:opacity-30">›</button>
        </div>
      )}
    </AppLayout>
  );
}

function UserRow({ u }) {
  return (
    <tr data-testid={`admin-user-row-${u.id}`} className="hover:bg-white/[0.03]">
      <td className="px-3 py-2.5 text-white whitespace-nowrap">{u.username}{u.is_admin && <span className="ml-1 text-[9px] text-[#FF5722]">ADMIN</span>}</td>
      <td className="px-3 py-2.5 whitespace-nowrap">{u.email}</td>
      <td className="px-3 py-2.5 whitespace-nowrap">{fmt(u.created_at)}</td>
      <td className="px-3 py-2.5 whitespace-nowrap">{fmt(u.password_changed_at)}{u.password_never_changed && <span className="text-white/40"> · {_t("mai cambiata")}</span>}</td>
      <td className="px-3 py-2.5 whitespace-nowrap"><Yes ok={u.email_verified} /> <span className="text-white/50">{u.email_verified ? fmt(u.email_verified_at) : ""}</span></td>
      <td className="px-3 py-2.5 whitespace-nowrap"><Yes ok={u.terms_privacy_accepted} /> <span className="text-white/50">{fmt(u.terms_privacy_at)}</span></td>
      <td className="px-3 py-2.5 whitespace-nowrap">{u.active ? <span className="text-emerald-400">{_t("Attivo")}</span> : <span className="text-red-400">{_t("Bloccato")}</span>}</td>
      <td className="px-3 py-2.5 whitespace-nowrap">{u.cookie_choice ? <>{_t(COOKIE[u.cookie_choice])} <span className="text-white/50">{fmt(u.cookie_at)}</span></> : <span className="text-white/40">{_t("Nessuna scelta")}</span>}</td>
      <td className="px-3 py-2.5 whitespace-nowrap text-white/60">T <Yes ok={u.reg_terms} /> P <Yes ok={u.reg_privacy} /> M <Yes ok={u.reg_marketing} /></td>
    </tr>
  );
}
